import React from 'react';

import { useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { reverse } from 'named-urls';

import { colors } from 'theme/colors';

import API from 'utils/api';

import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';

import { StyledTextField } from 'components/Input/styles';
import InfiniteAutocomplete from 'components/InfiniteAutocomplete';
import { SearchRequest, SearchResponse, UuidableName } from 'utils/api/types';

import { Button } from 'components/Button';

import DataError from 'containers/Error/DataError';

import routes from 'core/routes';

import { useStore } from 'utils/hooks/store';

import logo from '../../../assets/images/VatixLogoPublicSubmission.png';
import { HeaderTitle, MainContainer, HeaderWrapper, LogoContainer, Overview, SubmissionContainer } from '../styles';
import { Description, Header, StyledMenuItem, Warning, Wrapper, InputLabel } from './styles';

const FORM_PICKER_ID = 'public-form-dropdown-field';

const EntitiesFormPublicSubmissionList = (): React.ReactElement => {
  const params = useParams<{ publicUuid: string }>();

  const { routing } = useStore();

  const { publicUuid } = params;

  const request = React.useCallback(
    (searchParams?: SearchRequest) =>
      API.entities.loadPublicFormList(publicUuid as string, { ...searchParams, ordering: 'name' }),
    []
  );

  const {
    results: listOfPublicForms,
    loading,
    loadingMore,
    hasMore,
    failed,
    failedStatus,
    appliedQuery,
    loadMore,
    searchQuery,
    retry,
  } = useInfiniteSearch(request);

  const noAvailableForms = listOfPublicForms.length === 0;
  const initialLoadFailed = failed && noAvailableForms && !appliedQuery;

  const [selectedEntity, setSelectedEntity] = React.useState<UuidableName | null | undefined>();
  const [hasUserInteracted, setHasUserInteracted] = React.useState(false);

  const onChangeSearch = (s: string): void => {
    setHasUserInteracted(true);
    searchQuery(s);
  };

  const isOptionSelected = (option: UuidableName): boolean => {
    if (!selectedEntity) {
      return false;
    }
    return selectedEntity.uuid === option.uuid;
  };

  const handleRenderOption = React.useMemo(
    () =>
      (props: React.HTMLAttributes<HTMLLIElement>, option: UuidableName): React.ReactNode => {
        const { uuid, name } = option;
        const { ...restProps } = props;
        return (
          <Box sx={{ alignItems: 'center' }}>
            <StyledMenuItem key={`${uuid}`} {...restProps} value={uuid}>
              {name}
            </StyledMenuItem>
          </Box>
        );
      },
    [selectedEntity]
  );

  const onStartReport = (): void => {
    if (selectedEntity?.uuid) {
      routing.push(reverse(routes.publicForm.details, { publicFormId: selectedEntity?.uuid }));
    }
  };

  const renderContent = (): React.ReactElement => {
    if (initialLoadFailed && failedStatus === 404) {
      return (
        <Wrapper>
          <Header>{"This link isn't valid"}</Header>
          <Description>Ask the person who shared it with you for a new link.</Description>
        </Wrapper>
      );
    }

    if (initialLoadFailed) {
      return (
        <Wrapper>
          <DataError onReload={retry} />
        </Wrapper>
      );
    }

    return (
      <>
        <Wrapper>
          <Header>Report New Event</Header>
          <Description>Choose a form to begin your event submission.</Description>
          <InputLabel htmlFor={FORM_PICKER_ID}>Public form</InputLabel>

          <InfiniteAutocomplete
            loadingMore={loadingMore}
            hasMore={hasMore}
            onLoadMore={loadMore}
            disabled={noAvailableForms && !hasUserInteracted}
            id={FORM_PICKER_ID}
            options={listOfPublicForms}
            value={selectedEntity}
            renderOption={handleRenderOption}
            getOptionLabel={(option: SearchResponse) => option?.name as string}
            getOptionDisabled={(option: SearchResponse) => isOptionSelected(option)}
            renderInput={(inputParams) => (
              <StyledTextField
                {...inputParams}
                onChange={(e) => onChangeSearch(e.target.value)}
                InputProps={{
                  ...inputParams.InputProps,
                  type: 'search',
                }}
                placeholder="Search"
              />
            )}
            loading={loading}
            fullWidth
            disableClearable={!selectedEntity}
            onChange={(_, newValue) => {
              setSelectedEntity(newValue as UuidableName | null);
            }}
          />

          {noAvailableForms && !loading && !hasUserInteracted && (
            <Warning>
              <WarningAmberIcon htmlColor={colors.warning.main} />
              <p>No public forms available at this time</p>
            </Warning>
          )}
        </Wrapper>

        <Button
          fullWidth
          size="large"
          variant="contained"
          onClick={onStartReport}
          disabled={noAvailableForms || !selectedEntity}
        >
          Start Report
        </Button>
      </>
    );
  };

  return (
    <MainContainer>
      <LogoContainer>
        <img alt="Vatix logo" src={logo} />
      </LogoContainer>
      <HeaderWrapper>
        <HeaderTitle>New Submission</HeaderTitle>
      </HeaderWrapper>

      <Overview>
        <SubmissionContainer>{renderContent()}</SubmissionContainer>
      </Overview>
    </MainContainer>
  );
};

export default EntitiesFormPublicSubmissionList;
